import type { OrderResponse } from '@palaccount/contracts';
import { Link2, Receipt, Truck } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { LifecycleStepper, LinkedDocChip, ProgressLine, type LinkedDoc } from '@/components/document-list';

import type { OrderActs } from './order-actions';
import { lifecycleSteps } from './order-stepper';

/**
 * Wave 4B — the order's PLACE IN THE CHAIN, drawn the same way on the list
 * page and on the form: the linked-document chips (the quotation it came
 * from, the invoice it made, every delivery note raised on it), the
 * five-stage order-to-cash stepper (D-09: a missing stage that is the next
 * sensible act IS the act) and BR-SAL-02-03's "60% delivered · 0% invoiced".
 *
 * The list page feeds these three into `DocumentListPage`'s own slots; the
 * form mounts `OrderLifecycle`, which composes the same three primitives.
 */
export function orderLinks(order: OrderResponse, t: TFunction): LinkedDoc[] {
  return [
    ...(order.sourceQuotationCode
      ? [
          {
            key: 'quote',
            to: `/sales/quotes/${order.sourceQuotationId}`,
            icon: Link2,
            tone: 'muted' as const,
            label: t('orders.fromQuote', { code: order.sourceQuotationCode }),
          },
        ]
      : []),
    /* Every delivery note, oldest first — a cancelled one says so. */
    ...order.deliveryNotes.map((note) => ({
      key: `dn-${note.id}`,
      to: `/sales/deliveries/${note.id}`,
      icon: Truck,
      tone: note.status === 'cancelled' ? ('muted' as const) : ('blue' as const),
      label:
        note.status === 'cancelled' ? (
          t('orders.deliveryNoteCancelled', { code: note.code })
        ) : (
          <bdi dir="ltr">{note.code}</bdi>
        ),
    })),
    /* BR-SAL-02-08: the invoice pill OPENS the invoice. */
    ...(order.invoiceCode
      ? [
          {
            key: 'invoice',
            to: `/sales/${order.invoiceId}`,
            icon: Receipt,
            tone: 'blue' as const,
            label: <bdi dir="ltr">{order.invoiceCode}</bdi>,
          },
        ]
      : []),
  ];
}

/** BR-SAL-02-03 — "60% delivered · 0% invoiced". */
export function orderProgress(order: OrderResponse, t: TFunction): string {
  return t('orders.progress', {
    delivered: order.deliveredPct.replace(/\.00$/, ''),
    invoiced: order.invoicedPct.replace(/\.00$/, ''),
  });
}

/** The form's strip: chips, stepper, progress — the list page's detail header, on the editor. */
export function OrderLifecycle({ order, acts }: { order: OrderResponse; acts: OrderActs }) {
  const { t } = useTranslation();
  const links = orderLinks(order, t);
  return (
    <div className="grid gap-3 rounded-xl border bg-card p-4">
      {links.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2" aria-label={t('orders.linkedDocuments')}>
          {links.map((doc) => (
            <LinkedDocChip key={doc.key} doc={doc} />
          ))}
        </div>
      ) : null}
      <LifecycleStepper
        label={t('orders.stepper')}
        steps={lifecycleSteps(order, { createInvoice: acts.createInvoice, busy: acts.busy })}
      />
      <ProgressLine text={orderProgress(order, t)} />
    </div>
  );
}
